/* eslint-disable react-hooks/exhaustive-deps */
import { Box, HStack, Tab, TabList, Tabs } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, Heading } from '@/components/v4';
import { useMultiStyleConfig } from '@/components/v4/CustomSelect/useMultiStyleConfig';
import ImageCropperModal from './ImageCropperModal';
import LinksTab from './LinksTab';
import LogosTab from './LogosTab';

import {
    customTabStylesCombineTheme,
    selectedTabStylesCombineTheme,
} from '@/constants';
import { dataURLtoFile } from 'utils/utils';
import { axios } from '@/services/axios';

export default function PartnerBranding({ partner, onBack }) {
    const dispatch = useDispatch();
    const styles = useMultiStyleConfig('CustomSelect', {});

    // Data from Redux
    const { defaultCompany } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    // Navigation State
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Section Expansion States
    const [isLogosExpanded, setIsLogosExpanded] = useState(false);
    const [isBackgroundExpanded, setIsBackgroundExpanded] = useState(false);

    // Branding & Logo States
    const [logoSet, setLogoSet] = useState({
        light: null,
        dark: null,
        favicon: null,
    });
    const [defaultLogoSet, setDefaultLogoSet] = useState({
        light: null,
        dark: null,
        favicon: null,
    });
    const [brandingSet, setBrandingSet] = useState(null);
    const [linksData, setLinksData] = useState({
        extLinkSupport: '',
        extLinkRegistrationTerms: '',
        extLinkPrivacyPolicy: '',
    });

    // Files to upload
    const [filesToUpload, setFilesToUpload] = useState({
        light: null,
        dark: null,
        favicon: null,
        bg: null,
    });

    // Metadata for display
    const [metaData, setMetaData] = useState({
        light: { width: 0, height: 0, type: 'PNG' },
        dark: { width: 0, height: 0, type: 'PNG' },
        favicon: { width: 0, height: 0, type: 'PNG' },
        bg: '',
    });

    const [submitted, setSubmitted] = useState('idle');

    // Cropping Modal State
    const [cropModal, setCropModal] = useState({
        isOpen: false,
        imageSrc: null,
        target: null,
        aspectRatio: 2 / 1,
        maxWidth: 300,
        maxHeight: 150,
    });

    // Refs for file inputs
    const lightLogoInputRef = useRef(null);
    const darkLogoInputRef = useRef(null);
    const faviconInputRef = useRef(null);
    const bgImageInputRef = useRef(null);
    const colorPickerRef = useRef(null);

    // Preview state
    const [previewTab, setPreviewTab] = useState('Login');
    const [showPassword, setShowPassword] = useState(false);

    // Initial Data Fetch
    useEffect(() => {
        if (partner) getPartnerData();
    }, [partner]);

    const getPartnerData = async () => {
        if (!partner) return;
        const hostHeader = partner.hostHeader || 'unknown';
        try {
            const response = await axios.get('/theme/' + hostHeader);
            const brandingData = response.data;
            if (!brandingData) return;

            const logoFile = partner.logoFile || null;
            const darkLogoFile = partner.darkLogoFile || null;
            const faviconFile = partner.faviconFile || null;
            const partnerBranding = partner.partnerBranding || {};

            const logoUrls = {
                light: logoFile
                    ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${logoFile}`
                    : `${import.meta.env.VITE_APP_IMAGE_BASE}${brandingData.lightLogo}`,
                dark: darkLogoFile
                    ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${darkLogoFile}`
                    : `${import.meta.env.VITE_APP_IMAGE_BASE}${brandingData.darkLogo}`,
                favicon: faviconFile
                    ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${faviconFile}`
                    : `${import.meta.env.VITE_APP_IMAGE_BASE}${brandingData.favicon}`,
            };

            setLogoSet(logoUrls);
            setDefaultLogoSet(logoUrls);
            setBrandingSet({
                ...brandingData,
                backgroundType:
                    partnerBranding.backgroundType ??
                    brandingData.backgroundType,
                backgroundImage: partnerBranding.backgroundImage
                    ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${partnerBranding.backgroundImage}`
                    : `${import.meta.env.VITE_APP_IMAGE_BASE}${brandingData.backgroundImage}`,
                backgroundColor: partnerBranding.backgroundColor
                    ? `#${partnerBranding.backgroundColor}`
                    : `#${brandingData.backgroundColor}`,
            });
            setLinksData({
                extLinkSupport: partnerBranding.extLinkSupport || '',
                extLinkRegistrationTerms:
                    partnerBranding.extLinkRegistrationTerms || '',
                extLinkPrivacyPolicy:
                    partnerBranding.extLinkPrivacyPolicy || '',
            });

            // Initialize metadata for existing images
            ['light', 'dark', 'favicon'].forEach((target) => {
                const img = new window.Image();
                img.onload = () => {
                    setMetaData((prev) => ({
                        ...prev,
                        [target]: {
                            width: img.width,
                            height: img.height,
                            type: 'IMG',
                        },
                    }));
                };
                img.src = logoUrls[target];
            });
            return brandingData;
        } catch (error) {
            console.error('Error fetching partner data:', error);
        }
    };

    const handleLogoReset = (key) => {
        const defaultUrl = defaultLogoSet[key];
        setLogoSet((prev) => ({ ...prev, [key]: defaultUrl }));
        setFilesToUpload((prev) => ({ ...prev, [key]: null }));
        const img = new window.Image();
        img.onload = () => {
            setMetaData((prev) => ({
                ...prev,
                [key]: { width: img.width, height: img.height, type: 'IMG' },
            }));
        };
        img.src = defaultUrl;
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            dispatch({ type: 'RESET_MODAL' });
        }
    };

    const handleAssetSelect = (event, target) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setCropModal({
                    isOpen: true,
                    imageSrc: reader.result,
                    target,
                    aspectRatio: target === 'favicon' ? 1 : 2 / 1,
                    maxWidth: target === 'favicon' ? 192 : 300,
                    maxHeight: target === 'favicon' ? 192 : 150,
                });
            };
            reader.readAsDataURL(file);
            event.target.value = '';
        }
    };

    const onCropComplete = (croppedDataUrl, finalWidth, finalHeight) => {
        const target = cropModal.target;
        const filename = `${target}-logo.png`;
        const file = dataURLtoFile(croppedDataUrl, filename);

        setLogoSet((prev) => ({ ...prev, [target]: croppedDataUrl }));
        setFilesToUpload((prev) => ({ ...prev, [target]: file }));
        setMetaData((prev) => ({
            ...prev,
            [target]: { width: finalWidth, height: finalHeight, type: 'PNG' },
        }));
        setCropModal({ ...cropModal, isOpen: false });
    };

    const handleBgUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'image/png') {
                toast.error(
                    'Only PNG files are accepted for the background image',
                );
                event.target.value = '';
                return;
            }
            const objectUrl = URL.createObjectURL(file);
            const img = new window.Image();
            img.onload = () => {
                setBrandingSet((prev) => ({
                    ...prev,
                    backgroundImage: objectUrl,
                }));
                setFilesToUpload((prev) => ({ ...prev, bg: file }));
                const type = file.type.split('/')[1].toUpperCase();
                setMetaData((prev) => ({
                    ...prev,
                    bg: `${type} • ${img.width} x ${img.height} px`,
                }));
            };
            img.src = objectUrl;
        }
    };

    const updatePartnerbranding = async () => {
        setSubmitted('pending');
        const endpoint = `/partner/${partner.id}/logo`;
        const bgColor = brandingSet.backgroundColor.replace('#', '');
        const urlPattern = /^(http|https):\/\//i;

        if (
            linksData.extLinkRegistrationTerms &&
            !urlPattern.test(linksData.extLinkRegistrationTerms)
        ) {
            toast.error('Invalid Terms URL');
            setSubmitted('idle');
            return;
        }
        if (
            linksData.extLinkPrivacyPolicy &&
            !urlPattern.test(linksData.extLinkPrivacyPolicy)
        ) {
            toast.error('Invalid Privacy URL');
            setSubmitted('idle');
            return;
        }

        try {
            const currentPartner = await axios.get(`/partner/${partner.id}`);
            await axios.put('/partner', {
                ...currentPartner.data,
                partnerBranding: {
                    ...currentPartner.data.partnerBranding,
                    backgroundType: brandingSet.backgroundType,
                    backgroundColor: bgColor,
                    extLinkSupport: linksData.extLinkSupport,
                    extLinkRegistrationTerms:
                        linksData.extLinkRegistrationTerms,
                    extLinkPrivacyPolicy: linksData.extLinkPrivacyPolicy,
                },
            });

            // Upload Files
            const uploadTasks = [];
            if (filesToUpload.light) {
                const fd = new FormData();
                fd.append('file', filesToUpload.light);
                uploadTasks.push(
                    axios.post(endpoint, fd, { params: { mode: 'light' } }),
                );
            }
            if (filesToUpload.dark) {
                const fd = new FormData();
                fd.append('file', filesToUpload.dark);
                uploadTasks.push(
                    axios.post(endpoint, fd, { params: { mode: 'dark' } }),
                );
            }
            if (filesToUpload.favicon) {
                const fd = new FormData();
                fd.append('file', filesToUpload.favicon);
                uploadTasks.push(
                    axios.post(endpoint, fd, { params: { mode: 'favicon' } }),
                );
            }
            if (filesToUpload.bg) {
                const fd = new FormData();
                fd.append('file', filesToUpload.bg);
                uploadTasks.push(
                    axios.post(endpoint, fd, { params: { mode: 'BG' } }),
                );
            }

            await Promise.all(uploadTasks);

            if (partner.id === defaultCompany) {
                dispatch({
                    type: 'SET_SUPPORT_URL',
                    payload: linksData.extLinkSupport,
                });
            }

            setSubmitted('success');
            toast.success('Branding updated successfully');
            setTimeout(handleBack, 1000);
        } catch (error) {
            console.error(error);
            setSubmitted('idle');
            toast.error(error.response?.data?.message || 'Update failed');
        }
    };

    const getPreviewBackground = () => {
        if (!brandingSet) return {};
        switch (brandingSet.backgroundType) {
            case 1:
                return { backgroundColor: brandingSet.backgroundColor };
            case 2:
                return {
                    backgroundImage: `url(${brandingSet.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                };
            case 3:
                return {
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1000')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                };
            case 4:
                return {
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&q=80&w=1000')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                };
            default:
                return { backgroundColor: '#f1f5f9' };
        }
    };

    const isColorDark = (hex) => {
        if (!hex || hex.length < 6) return false;
        const cleanHex = hex.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return (r * 299 + g * 587 + b * 114) / 1000 < 128;
    };

    const addToken = (token) => {
        setLinksData((prev) => ({
            ...prev,
            extLinkSupport: prev.extLinkSupport + `[${token}]`,
        }));
    };

    const availableFields = [
        { label: 'Company ID', value: 'COMPANY_ID' },
        { label: 'Partner ID', value: 'PARTNER_ID' },
        { label: 'User ID', value: 'USER_ID' },
        { label: 'Company Name', value: 'COMPANY_NAME' },
        { label: 'Company Legal Entity Name', value: 'COMPANY_LEGAL_NAME' },
        { label: 'User Email', value: 'USER_EMAIL' },
        { label: 'User First Name', value: 'USER_FIRST_NAME' },
        { label: 'User Last Name', value: 'USER_LAST_NAME' },
        { label: "Company's Attribute 1", value: 'COMPANY_ATTR_1' },
        { label: "Company's Attribute 2", value: 'COMPANY_ATTR_2' },
        { label: "Company's Attribute 3", value: 'COMPANY_ATTR_3' },
        { label: 'Trial Status', value: 'TRIAL_STATUS' },
    ];

    if (!brandingSet) return null;

    return (
        <Box minH="100vh" w="full" position="relative">
            <ImageCropperModal
                {...cropModal}
                onClose={() => setCropModal({ ...cropModal, isOpen: false })}
                onApply={onCropComplete}
            />

            <input
                type="file"
                ref={lightLogoInputRef}
                style={{ display: 'none' }}
                accept=".png"
                onChange={(e) => handleAssetSelect(e, 'light')}
            />
            <input
                type="file"
                ref={darkLogoInputRef}
                style={{ display: 'none' }}
                accept=".png"
                onChange={(e) => handleAssetSelect(e, 'dark')}
            />
            <input
                type="file"
                ref={faviconInputRef}
                style={{ display: 'none' }}
                accept=".ico,.png"
                onChange={(e) => handleAssetSelect(e, 'favicon')}
            />
            <input
                type="file"
                ref={bgImageInputRef}
                style={{ display: 'none' }}
                accept=".png"
                onChange={handleBgUpload}
            />

            <Box maxW="895px" w="full">
                <Heading size="md" mb="15px" fontWeight="medium">
                    Partner Branding
                </Heading>
                <Tabs
                    index={activeTabIndex}
                    onChange={setActiveTabIndex}
                    variant="unstyled">
                    <TabList>
                        {['Logos', 'Links'].map((tab, idx) => (
                            <Tab
                                key={`${tab}-${idx}`}
                                px={1}
                                pb={4}
                                sx={{
                                    ...customTabStylesCombineTheme,
                                    color: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}
                                _selected={{
                                    ...selectedTabStylesCombineTheme,
                                }}>
                                {tab}
                            </Tab>
                        ))}
                    </TabList>
                </Tabs>
            </Box>

            <Box
                maxW="895px"
                w="full"
                pt={8}
                pb={12}
                minH="calc(100vh - 200px)"
                display="flex"
                flexDirection="column">
                <Box flex="1">
                    {activeTabIndex === 0 ? (
                        <LogosTab
                            darkMode={darkMode}
                            logoSet={logoSet}
                            defaultLogoSet={defaultLogoSet}
                            isLogosExpanded={isLogosExpanded}
                            setIsLogosExpanded={setIsLogosExpanded}
                            isBackgroundExpanded={isBackgroundExpanded}
                            setIsBackgroundExpanded={setIsBackgroundExpanded}
                            metaData={metaData}
                            brandingSet={brandingSet}
                            setBrandingSet={setBrandingSet}
                            lightLogoInputRef={lightLogoInputRef}
                            darkLogoInputRef={darkLogoInputRef}
                            faviconInputRef={faviconInputRef}
                            bgImageInputRef={bgImageInputRef}
                            colorPickerRef={colorPickerRef}
                            handleLogoReset={handleLogoReset}
                            previewTab={previewTab}
                            setPreviewTab={setPreviewTab}
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                            getPreviewBackground={getPreviewBackground}
                            isColorDark={isColorDark}
                            styles={styles}
                        />
                    ) : (
                        <LinksTab
                            darkMode={darkMode}
                            linksData={linksData}
                            setLinksData={setLinksData}
                            availableFields={availableFields}
                            addToken={addToken}
                        />
                    )}
                </Box>

                <HStack spacing={4} mt={12}>
                    <Button
                        px={12}
                        h="12"
                        colorScheme="messenger"
                        fontWeight="bold"
                        shadow="lg"
                        _active={{ transform: 'scale(0.95)' }}
                        onClick={updatePartnerbranding}
                        isLoading={submitted === 'pending'}>
                        Save
                    </Button>
                    <Button
                        px={12}
                        h="12"
                        variant="outline"
                        colorScheme="messenger"
                        fontWeight="bold"
                        bg="white"
                        _active={{ transform: 'scale(0.95)' }}
                        onClick={handleBack}>
                        Back
                    </Button>
                </HStack>
            </Box>
        </Box>
    );
}
